<template>
  <div class="register-step">
    <div class="step-header">
      <h2>选择平台</h2>
      <p class="subtitle">请选择您要注册的平台</p>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="platform-loading">
      <div class="loading-spinner"></div>
      <p>正在加载平台列表...</p>
    </div>

    <!-- 空状态 -->
    <div v-else-if="!platforms || platforms.length === 0" class="platform-empty">
      <i class="el-icon-warning-outline"></i>
      <p>暂无可用平台</p>
    </div>

    <!-- 平台列表 -->
    <div v-else class="platform-list">
      <div
        v-for="item in platforms"
        :key="item.id"
        class="platform-card"
        :class="{ selected: selectedPlatform && selectedPlatform.id === item.id }"
        @click="$emit('select', item)"
      >
        <div class="platform-logo">
          <img v-if="item.logo" :src="item.logo" :alt="item.name" />
          <div v-else class="logo-placeholder">
            {{ (item.name || '').substring(0, 1) }}
          </div>
        </div>
        <div class="platform-info">
          <span class="platform-name">{{ item.name }}</span>
          <span v-if="item.platformType" class="platform-type">{{ item.platformType }}</span>
        </div>
        <div class="platform-check">
          <span v-if="selectedPlatform && selectedPlatform.id === item.id" class="check-icon">✓</span>
        </div>
      </div>
    </div>

    <div class="btn-area">
      <button
        class="submit-btn"
        :disabled="!selectedPlatform"
        @click="$emit('next')"
      >
        下一步
        <span class="btn-arrow">→</span>
      </button>
    </div>
  </div>
</template>

<script>
/**
 * H5渠道注册 - 平台选择步骤组件（SaaS化统一包新增）
 * 作者：智慧云行
 */
export default {
  name: 'PlatformSelectStep',
  props: {
    platforms: {
      type: Array,
      default: () => []
    },
    selectedPlatform: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  }
}
</script>

<style scoped>
.register-step {
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 24px 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1;
}

.step-header {
  text-align: center;
  margin-bottom: 20px;
}

.step-header h2 {
  color: #fff;
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 6px;
  letter-spacing: 1px;
}

.subtitle {
  color: rgba(255, 255, 255, 0.75);
  font-size: 13px;
  margin: 0;
}

/* 加载状态 */
.platform-loading {
  text-align: center;
  padding: 40px 0;
  color: rgba(255, 255, 255, 0.7);
}

.loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  margin: 0 auto 12px;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.platform-loading p {
  font-size: 13px;
  margin: 0;
}

/* 空状态 */
.platform-empty {
  text-align: center;
  padding: 40px 0;
  color: rgba(255, 255, 255, 0.6);
}

.platform-empty i {
  font-size: 48px;
  display: block;
  margin-bottom: 12px;
}

.platform-empty p {
  font-size: 14px;
  margin: 0;
}

/* 平台列表 */
.platform-list {
  max-height: 280px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 16px;
}

.platform-list::-webkit-scrollbar {
  width: 4px;
}

.platform-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
}

.platform-card {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  margin-bottom: 10px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.25s ease;
}

.platform-card:last-child {
  margin-bottom: 0;
}

.platform-card:active {
  transform: scale(0.98);
}

.platform-card.selected {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.6);
  box-shadow: 0 4px 16px rgba(255, 255, 255, 0.15);
}

.platform-logo {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;
  margin-right: 14px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
}

.platform-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.logo-placeholder {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
}

.platform-info {
  flex: 1;
  min-width: 0;
}

.platform-name {
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.platform-type {
  display: inline-block;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
}

.platform-check {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.platform-check .check-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7EBF50 0%, #6AAD40 100%);
  color: #fff;
  font-size: 14px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(126, 191, 80, 0.4);
  animation: checkIn 0.3s ease;
}

@keyframes checkIn {
  from { transform: scale(0); }
  to { transform: scale(1); }
}

/* 按钮区域 */
.btn-area {
  margin-top: 20px;
}

.submit-btn {
  width: 100%;
  height: 48px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #fff 0%, #f0f4ff 100%);
  color: #2B77FF;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 255, 255, 0.3);
  letter-spacing: 1px;
}

.submit-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.submit-btn:not(:disabled):active {
  transform: scale(0.98);
}

.btn-arrow {
  font-size: 18px;
  transition: transform 0.3s ease;
}

.submit-btn:not(:disabled):hover .btn-arrow {
  transform: translateX(4px);
}

@media screen and (max-width: 480px) {
  .register-step {
    padding: 20px 16px;
  }

  .step-header h2 {
    font-size: 18px;
  }

  .platform-card {
    padding: 12px 14px;
  }

  .platform-logo {
    width: 40px;
    height: 40px;
    margin-right: 12px;
  }

  .platform-name {
    font-size: 14px;
  }
}

@media screen and (max-width: 360px) {
  .register-step {
    padding: 16px 12px;
  }

  .platform-list {
    max-height: 220px;
  }
}
</style>
